import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Stack,
  Typography,
} from '@mui/material'
import { useMemo } from 'react'
import { useLocation } from 'wouter'

import { usePaginatedCollection, useUser } from '@/pb'
import StatCard from './StatCard'
import StatChart from './StatChart'
import { enrichWorkout, fmtDate, getWorkoutTitle } from './util'

export default function HomeScreen() {
  const { user } = useUser()
  const [, navigate] = useLocation()

  const {
    records: workoutTemplates = [],
    loaded: workoutTemplatesLoaded,
  } = usePaginatedCollection('music_workout_templates', {
    page: 1,
    perPage: 30,
    sort: '-created',
    expand: 'musicId',
  })

  const {
    records: workoutHistory = [],
    loaded: workoutHistoryLoaded,
  } = usePaginatedCollection('music_workouts', {
    page: 1,
    perPage: 30,
    sort: '-created',
    filter: `userId = "${user.id}"`,
  })

  const workouts = useMemo(() => {
    if (!workoutTemplatesLoaded || !workoutHistoryLoaded) return []

    return workoutHistory.map(workout =>
      enrichWorkout(workout, workoutTemplates)
    )
  }, [
    workoutHistory,
    workoutTemplates,
    workoutHistoryLoaded,
    workoutTemplatesLoaded,
  ])

  const lastWorkoutEntry = workouts.at(0)

  const lastWorkoutTemplate =
    lastWorkoutEntry?.template ||
    workoutTemplates.find(
      w => w.id === lastWorkoutEntry?.templateId
    )

  const workoutTitle =
    lastWorkoutEntry?.workoutTitle ||
    lastWorkoutTemplate?.expand?.musicId?.title ||
    getWorkoutTitle(lastWorkoutTemplate)

  const hasPrivilegedRole =
    user.role?.includes('admin') ||
    user.role?.includes('trainer')

  const currentMusicWorkoutId = user?.currentMusicWorkoutId

  return (
    <Box sx={{ p: 2 }}>

      {
        currentMusicWorkoutId ? (
          <Button
            fullWidth
            variant="contained"
            size="large"
            onClick={() =>
              navigate(`/app/workout/${currentMusicWorkoutId}`)
            }
          >
            Перейти в текущую тренировку
          </Button>
        ) :
          (
            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={() => navigate('/app/start')}
            >
              Начать тренировку
            </Button>
          )
      }


      <Card variant="outlined" sx={{ mt: 2, borderRadius: 3 }}>
        <CardContent>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 1 }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
              Последняя тренировка
            </Typography>

            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                onClick={() => navigate('/app/history')}
              >
                Архив
              </Button>

              {lastWorkoutEntry && (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() =>
                    navigate(`/app/workout/${lastWorkoutEntry.id}`)
                  }
                >
                  Подробнее
                </Button>
              )}
            </Stack>
          </Stack>

          {lastWorkoutEntry ? (
            <>
              <Typography variant="body1" sx={{ fontWeight: 700 }}>
                {workoutTitle} (
                {fmtDate(
                  lastWorkoutEntry.created ||
                  lastWorkoutEntry.updated ||
                  lastWorkoutEntry.startTime
                )}
                )
              </Typography>

              <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
                <Grid item xs={4}>
                  <StatCard
                    label="Эффективность"
                    value={`${lastWorkoutEntry.efficiency ?? 0}%`}
                  />
                </Grid>

                <Grid item xs={4}>
                  <StatCard
                    label="Средняя сила"
                    value={lastWorkoutEntry.avgForce ?? '0'}
                  />
                </Grid>

                <Grid item xs={4}>
                  <StatCard
                    label="Среднее отставание"
                    value={`${lastWorkoutEntry.averageDelay ?? 0}с`}
                  />
                </Grid>
              </Grid>
            </>
          ) : (
            <Typography color="text.secondary">
              Пока что нет тренировок
            </Typography>
          )}
        </CardContent>
      </Card>

      <Card variant="outlined" sx={{ mt: 2, borderRadius: 3 }}>
        <CardContent>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 1 }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
              Аналитика
            </Typography>

            <Button
              size="small"
              variant="outlined"
              onClick={() => navigate('/app/analytics')}
            >
              Подробнее
            </Button>
          </Stack>

          <StatChart
            workouts={workouts}
            dataKey="efficiency"
            label="Эффективность"
            unit="%"
          />
        </CardContent>
      </Card>

      <Button
        sx={{ mt: 2 }}
        variant="contained"
        size="small"
        onClick={() => navigate('/app/account')}
      >
        Аккаунт
      </Button>

      {hasPrivilegedRole && (
        <Button
          sx={{ mt: 2, ml: 2 }}
          variant="contained"
          size="small"
          onClick={() => navigate('~/admin')}
          color="info"
        >
          Админ панель
        </Button>
      )}
    </Box>
  )
}